import { JSONObject } from '@/utils/types';

export type ErrorInfo = Record<string, string | number | boolean>;

/**
 * This type confuses multiple layers of error, and should be avoided in favor of ApiError and CalmError
 *
 * @deprecated
 */
export type LegacyCalmError = {
	calmCode?: string;
	calmMessage?: string;
	message?: string;
	code?: string;
	didTimeout?: boolean;
	isRateLimit?: boolean;
	info?: ErrorInfo;
	status?: string;
	httpCode?: number;
	data?: {
		error?: {
			code?: string;
			info?: ErrorInfo;
		};
	};
};

export const isLegacyCalmError = (info: Error | LegacyCalmError | unknown): info is LegacyCalmError =>
	typeof info === 'object' && info ? Object.keys(info).includes('data') : false;

export interface ApiValidationError {
	message: string;
	path: string[];
	type: string;
	context: JSONObject;
}

export interface CalmErrorPayload {
	code: string;
	info?: JSONObject & {
		validationErrors?: [ApiValidationError];
	};
}

export interface ApiError {
	headers: Record<string, string>;
	status: number;
	statusText: string;
	message?: string;
}

export const isApiError = (info: unknown): info is ApiError => {
	return (
		typeof info === 'object' &&
		info !== null &&
		Object.keys(info).includes('headers') &&
		Object.keys(info).includes('status') &&
		Object.keys(info).includes('statusText')
	);
};

export interface CalmError extends ApiError {
	data: {
		error: CalmErrorPayload;
	};
}

export const isCalmError = (info: unknown): info is CalmError => {
	if (!isApiError(info)) {
		return false;
	}
	if (!Object.keys(info).includes('data')) {
		return false;
	}
	const infoWithData = info as ApiError & { data: unknown };
	if (!(
		typeof infoWithData.data === 'object' &&
		infoWithData.data !== null &&
		Object.keys(infoWithData.data).includes('error')
	)) {
		return false;
	}
	const infoWithDataError = infoWithData as ApiError & {
		data: { error: unknown };
	};
	return (
		typeof infoWithDataError.data.error === 'object' &&
		infoWithDataError.data.error !== null &&
		Object.keys(infoWithDataError.data.error).includes('code')
	);
};

export const isAtlasSubscriptionsApiError = <TData>(info: unknown): info is ApiError & { data: TData } => {
	if (!isApiError(info)) return false;
	const infoWithData = info as ApiError & { data: unknown };
	return typeof infoWithData.data === 'object' && infoWithData.data !== null;
};
